import { useEffect, useMemo, useState } from 'react';
import { DialogIntro } from '@/features/dialog/DialogIntro';
import { shouldShowIntro } from '@/lib/dialog-intro';
import { useParams } from 'react-router-dom';
import { initAnalytics, track } from '@/lib/analytics';
import { useDialogSession } from '@/hooks/use-dialog';
import { useConfig } from '@/app/ConfigProvider';
import { DialogChat } from '@/features/dialog/DialogChat';
import { getOrCreateClientSessionId } from '@/lib/clientSessionId';
import { Loader2, AlertCircle } from 'lucide-react';

export default function DialogPage() {
  const { token } = useParams<{ token: string }>();
  // Single source of truth for the anonymous-actor id. Computed once per
  // mount so the same value flows to both useDialogSession (the GET) and
  // DialogChat (the streaming send). Avoids the localStorage-blocked
  // failure mode where independent computations in each component would
  // reroll fresh UUIDs and split persistence across two actor ids per
  // visit.
  const clientSessionId = useMemo(
    () => (token ? getOrCreateClientSessionId(token) : undefined),
    [token],
  );
  // The intro is dismissed per token, not per mount: React Router reuses this
  // page component across route-param changes, so a bare boolean would leak
  // one dialog's dismissal into the next.
  const [introDismissedFor, setIntroDismissedFor] = useState<string | null>(null);
  const { data, isLoading, error } = useDialogSession(token || '', clientSessionId);
  const config = useConfig();
  const streamUrl = config.api.streamUrl || null;

  // Analytics is initialised here rather than in ConfigProvider so the SDK never
  // loads on the authenticated pages — "dialog only" stays a structural fact
  // rather than a runtime branch.
  //
  // The guard matters for the funnel's denominator: an expired link, a closed
  // dialog, or an idea-coach session must not count as a respondent landing.
  const isActiveJtbdDialog =
    !isLoading && !error && data?.status === 'active' && data.mode !== 'idea-coach';

  useEffect(() => {
    if (!isActiveJtbdDialog) return;
    initAnalytics(config);
    track('dialog_loaded', { sessionId: clientSessionId });
  }, [isActiveJtbdDialog, config, clientSessionId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-b from-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data || data.status !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-b from-background to-muted">
        <div className="w-full max-w-md p-8 bg-card rounded-xl shadow-lg border text-center space-y-4">
          <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">Dialog unavailable</h1>
          <p className="text-sm text-muted-foreground">
            This dialog link is no longer active. It may have expired or been closed by the project owner.
          </p>
        </div>
      </div>
    );
  }

  // Idea-coach dialogs are PM-only and driven from step 1 — their tokens are
  // never distributed to this public route. If one is opened here anyway, the
  // JTBD DialogChat would mis-read the coach's `ideaDraft` state as JTBD items
  // and crash, so short-circuit with a friendly redirect instead.
  if (data.mode === 'idea-coach') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-b from-background to-muted">
        <div className="w-full max-w-md p-8 bg-card rounded-xl shadow-lg border text-center space-y-4">
          <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">Open the idea coach from your project</h1>
          <p className="text-sm text-muted-foreground">
            This is an idea-coaching session, which runs inside step 1 of your project — not from a shared link.
          </p>
        </div>
      </div>
    );
  }

  if (introDismissedFor !== token && shouldShowIntro(data)) {
    return (
      <DialogIntro
        framing={data.respondentFraming}
        onStart={() => {
          track('dialog_intro_continued', { sessionId: clientSessionId });
          setIntroDismissedFor(token!);
        }}
      />
    );
  }

  // key={token} forces a remount when the route param changes, so
  // local state (messages, draftItems, clientSessionId) doesn't bleed
  // across dialogs if React Router reuses the page component.
  return <DialogChat key={token} token={token!} dialogInfo={data} streamUrl={streamUrl} clientSessionId={clientSessionId} />;
}
